/**
 * Kräver åtgärd: service, besiktning, leasing och fordonsskatt i ett kort.
 * Bara designförhandsvisningen byter ut de fyra korten mot det här.
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { assignmentApi } from '@/services/assignments/assignmentApi';
import { serviceApi, type ServiceOverviewCount } from '@/services/service/serviceApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/store/authStore';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
import { ROUTES } from '@/constants/routes';
import {
  actionBarShare,
  actionChips,
  actionDetailTarget,
  sortActionRows,
  type ActionChip,
  type ActionCounts,
  type ActionKind,
} from '@/utils/actionRequired';
import ActionRequiredDetail from './ActionRequiredDetail';

interface ListResponse {
  success: boolean;
  data?: { vehicleId: number }[];
  meta?: { totalCount?: number; overdueCount?: number; daysAhead?: number; monthsAhead?: number };
}

const FEATURE_BY_KIND: Record<ActionKind, string> = {
  service: 'WIDGET_SERVICE_DUE',
  inspection: 'WIDGET_INSPECTION_DUE',
  leasing: 'WIDGET_LEASING_EXPIRING',
  tax: 'WIDGET_TAX_DUE',
};

const CHIP_CLASS: Record<ActionChip, string> = {
  overdue: 'text-rose-400',
  soon: 'text-amber-400',
  assigned: 'text-teal-400',
};

function vehicleIds(rows: { vehicleId: number }[] | undefined): number[] {
  return (rows ?? []).map((row) => Number(row.vehicleId)).filter((id) => Number.isFinite(id));
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

const ActionRequiredWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openKind, setOpenKind] = useState<Exclude<ActionKind, 'service'> | null>(null);
  const isGlobalAdmin = isGlobalAdminUser(useAuthStore((state) => state.user));
  const { hasActionAccess, isUnlimited } = usePermissions();
  const format = new Intl.NumberFormat(language === 'en' ? 'en-GB' : language === 'lt' ? 'lt-LT' : language === 'de' ? 'de-DE' : 'sv-SE');

  const allowed = (kind: ActionKind) => isGlobalAdmin || isUnlimited || hasActionAccess(FEATURE_BY_KIND[kind], 'VIEW');

  const service = useQuery(
    ['action-required', 'service'],
    async (): Promise<ServiceOverviewCount> => {
      try {
        return await serviceApi.getServiceOverviewCount();
      } catch (error) {
        if (isNotFound(error)) return { total: 0, overdue: 0, warning: 0, booked: 0 };
        throw error;
      }
    },
    { staleTime: 60 * 1000, enabled: allowed('service') }
  );

  const inspection = useQuery(
    ['action-required', 'inspection'],
    async () => {
      const response = await api.get<ListResponse>('/api/dashboard/data/upcoming-inspections', { params: { days: 40 } });
      if (!response.data.success) throw new Error('Failed to load inspections');
      return response.data;
    },
    { staleTime: 60 * 1000, enabled: allowed('inspection') }
  );

  const leasing = useQuery(
    ['action-required', 'leasing'],
    async () => {
      const response = await api.get<ListResponse>('/api/dashboard/data/upcoming-leasing-expirations');
      if (!response.data.success) throw new Error('Failed to load leasing');
      return response.data;
    },
    { staleTime: 60 * 1000, enabled: allowed('leasing') }
  );

  const tax = useQuery(
    ['action-required', 'tax'],
    async () => {
      const response = await api.get<ListResponse>('/api/dashboard/data/upcoming-tax-payments', { params: { days: 45 } });
      if (!response.data.success) throw new Error('Failed to load tax');
      return response.data;
    },
    { staleTime: 60 * 1000, enabled: allowed('tax') }
  );

  const inspectionIds = vehicleIds(inspection.data?.data);
  const leasingIds = vehicleIds(leasing.data?.data);
  const taxIds = vehicleIds(tax.data?.data);

  const inspectionAssigned = useQuery(
    ['action-required', 'assigned', 'INSPECTION', inspectionIds.join(',')],
    () => assignmentApi.getOpenByVehicleIds(inspectionIds, 'INSPECTION'),
    { enabled: allowed('inspection') && inspectionIds.length > 0, staleTime: 60 * 1000 }
  );
  const leasingAssigned = useQuery(
    ['action-required', 'assigned', 'LEASING', leasingIds.join(',')],
    () => assignmentApi.getOpenByVehicleIds(leasingIds, 'LEASING'),
    { enabled: allowed('leasing') && leasingIds.length > 0, staleTime: 60 * 1000 }
  );
  const taxAssigned = useQuery(
    ['action-required', 'assigned', 'TAX', taxIds.join(',')],
    () => assignmentApi.getOpenByVehicleIds(taxIds, 'TAX'),
    { enabled: allowed('tax') && taxIds.length > 0, staleTime: 60 * 1000 }
  );

  const assignedCount = (map: Record<number, unknown> | undefined) => (map ? Object.keys(map).length : 0);

  const rows = useMemo(() => {
    const built: ActionCounts[] = [];
    if (allowed('service')) {
      built.push({
        kind: 'service',
        total: service.data?.total ?? 0,
        overdue: service.data?.overdue ?? 0,
        soon: service.data?.warning ?? 0,
        assigned: 0,
      });
    }
    if (allowed('inspection')) {
      built.push({
        kind: 'inspection',
        total: inspection.data?.meta?.totalCount ?? inspection.data?.data?.length ?? 0,
        overdue: inspection.data?.meta?.overdueCount ?? 0,
        soon: 0,
        assigned: assignedCount(inspectionAssigned.data),
      });
    }
    if (allowed('leasing')) {
      built.push({
        kind: 'leasing',
        total: leasing.data?.meta?.totalCount ?? leasing.data?.data?.length ?? 0,
        overdue: leasing.data?.meta?.overdueCount ?? 0,
        soon: 0,
        assigned: assignedCount(leasingAssigned.data),
      });
    }
    if (allowed('tax')) {
      built.push({
        kind: 'tax',
        total: tax.data?.meta?.totalCount ?? tax.data?.data?.length ?? 0,
        overdue: tax.data?.meta?.overdueCount ?? 0,
        soon: 0,
        assigned: assignedCount(taxAssigned.data),
      });
    }
    return sortActionRows(built);
  }, [
    service.data,
    inspection.data,
    leasing.data,
    tax.data,
    inspectionAssigned.data,
    leasingAssigned.data,
    taxAssigned.data,
    isGlobalAdmin,
    isUnlimited,
    hasActionAccess,
  ]);

  const failed: Record<ActionKind, boolean> = {
    service: service.isError,
    inspection: inspection.isError,
    leasing: leasing.isError,
    tax: tax.isError,
  };
  const waiting: Record<ActionKind, boolean> = {
    service: service.isLoading && !service.data,
    inspection: inspection.isLoading && !inspection.data,
    leasing: leasing.isLoading && !leasing.data,
    tax: tax.isLoading && !tax.data,
  };
  const refreshing = service.isFetching || inspection.isFetching || leasing.isFetching || tax.isFetching;

  const hintFor = (kind: ActionKind) => {
    if (kind === 'service') return t('actionRequired.serviceHint');
    if (kind === 'inspection') {
      return t('actionRequired.inspectionHint').replace('{days}', String(inspection.data?.meta?.daysAhead ?? 40));
    }
    if (kind === 'leasing') {
      return t('actionRequired.leasingHint').replace('{months}', String(leasing.data?.meta?.monthsAhead ?? 6));
    }
    return t('actionRequired.taxHint').replace('{days}', String(tax.data?.meta?.daysAhead ?? 45));
  };

  const chipText = (chip: ActionChip, item: ActionCounts) => {
    const count = chip === 'overdue' ? item.overdue : chip === 'soon' ? item.soon : item.assigned;
    const key = chip === 'overdue' ? 'actionRequired.overdue' : chip === 'soon' ? 'actionRequired.soon' : 'actionRequired.assigned';
    return t(key).replace('{count}', format.format(count));
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-base font-semibold text-textDefault">{t('actionRequired.title')}</h2>
          <span className="shrink-0 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-300">
            {t('actionRequired.sortedByOverdue')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { void queryClient.invalidateQueries(['action-required']); }}
          className="rounded-lg p-1 text-textTertiary hover:bg-bgHover"
          aria-label={t('common.refresh')}
        >
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {rows.map((item) => {
          const share = actionBarShare(item);
          const leadClass = share.tone === 'overdue' ? 'bg-rose-400' : share.tone === 'assigned' ? 'bg-teal-400' : '';
          const value = failed[item.kind] || waiting[item.kind] ? '–' : format.format(item.total);
          return (
            <button
              key={item.kind}
              type="button"
              onClick={() => {
                if (actionDetailTarget(item.kind) === 'service') {
                  navigate(ROUTES.SERVICE);
                  return;
                }
                if (item.kind !== 'service') setOpenKind(item.kind);
              }}
              className="rounded-lg text-left hover:bg-bgHover"
              aria-label={t('actionRequired.openDetail').replace('{name}', t(`actionRequired.${item.kind}`))}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-textDefault">{t(`actionRequired.${item.kind}`)}</div>
                  <div className="truncate text-xs text-textSecondary">{hintFor(item.kind)}</div>
                </div>
                <div className="flex shrink-0 items-baseline gap-3">
                  {!failed[item.kind] && !waiting[item.kind] && actionChips(item).map((chip) => (
                    <span key={chip} className={`text-xs font-medium ${CHIP_CLASS[chip]}`}>
                      {chipText(chip, item)}
                    </span>
                  ))}
                  <span className="text-2xl font-semibold tabular-nums text-textDefault">{value}</span>
                </div>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-amber-500/70">
                {share.ratio > 0 && (
                  <div className={`h-full ${leadClass}`} style={{ width: `${share.ratio * 100}%` }} />
                )}
              </div>
            </button>
          );
        })}
      </div>
      {openKind && <ActionRequiredDetail kind={openKind} onClose={() => setOpenKind(null)} />}
    </div>
  );
};

export default ActionRequiredWidget;
